import {html} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {FormConfiguration} from "../../../advanced-form/form-configuration.type";
import {CreateIssueStatusCommand} from "../../../commands/create-issue-status.command";
import {ICommand} from "../../../commands/i-command.interface";
import {IssueStatus} from "../../../domain/entities/issue-status.entity";
import {AbstractEntityDialogComponent} from "../../ha-dialog/abstract-entity-dialog.component";
import {DialogCommandName} from "../../ha-dialog/dialog-command.enum";
import {DialogCommand} from "../../ha-dialog/dialog-command.interface";
import {IssueStatusUpdateCommandBuilder} from "./issue-status-update-command-builder";
import {issueStatusFormSettings} from "./issue-status-form-settings.const";
import {get, translate} from "lit-translate";

@customElement(`ha-dialog-issue-status-edit`)
export class HaDialogIssueStatusEditComponent extends AbstractEntityDialogComponent<IssueStatus> {

    public title = get('ha-boards-view.edit-status.header');

    protected _formSettings: FormConfiguration<IssueStatus>[] = issueStatusFormSettings;

    /** Sets entity and updates dialog title for creation mode. */
    public set entity(entity: IssueStatus | null) {
        super.entity = entity;
        if (this.creationMode) {
            this.title = get('ha-boards-view.edit-status.header-create');
        }
    }

    public get entity(): IssueStatus | null {
        return super.entity;
    }

    protected _renderMainContent(): unknown {
        const bindTo = this.form!.bindToFn;

        return html`
            <ha-form-issue-status>
                <header class="formular__header">
                    ${this._renderFormFooter()}
                </header>
                <main class="formular__main">
                    <ha-input-text class="max9999-flexrow" data-type="title" label=${translate('components.ha-dialog.title')}
                                    ${bindTo(`key`)}
                                    @value-changed=${this._requestRender}
                    ></ha-input-text>
                    <ha-input-text class="max9999-flexrow" data-type="shortcut"
                                    label=${translate('components.ha-dialog.shortcut')}
                                    maxlength="10"
                                    ${bindTo(`shortcut`)}
                    ></ha-input-text>
                    <ha-input-color class="max9999-flexrow" data-type="color"
                                    label=${translate('components.ha-dialog.color')}
                                    .entity=${IssueStatus}
                                    ${bindTo(`color`)}
                                    @value-changed=${this._requestRender}
                    ></ha-input-color>
                    ${this._renderTextColorField(this.form!.value.key ?? ``)}
                </main>
            </ha-form-issue-status>
        `;
    }

    protected _renderFormFooter(): unknown {
        return html`
            <div class="button-group">
                <button type="button"
                        class="save buttonform-marker primary"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickSaveButton}
                
                        data-label=${translate('general.save')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="save-and-close buttonform-marker primary"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickSaveAndCloseButton}
                
                        data-label=${translate('general.save-and-close')}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="reset buttonform-marker secondary"
                        ?disabled=${!this._isSavable}
                        @click=${this._onClickResetButton}
                
                        data-label=${translate('general.reset')}><div class="hover-effect"></div>
                </button>
            </div>
        `;
    }

    private _onClickSaveButton(): void {
        const data = this.form!.getChangedValues();

        if (!this._entity) {
            return;
        }

        // Capture the changed field keys BEFORE Object.assign mutates the
        // entity. Writing through the entity setters triggers
        // `_setOriginalValue`, which clears `entity.changedValues` and
        // would otherwise leave us unable to decide which update commands
        // to build (silent BulkCommand([]) regression).
        const changedFields = Object.keys(data);

        Object.assign(this._entity, data);

        this.dispatchCommand(<DialogCommand<ICommand<IssueStatus>>>{
            command: DialogCommandName.OKAY,
            payload: this._entity.isNew()
                ? new CreateIssueStatusCommand(this._entity)
                : IssueStatusUpdateCommandBuilder.build(this._entity, changedFields)
        });
    }

    private _onClickResetButton(): void {
        this.form?.reset();
    }

    /** Saves the entity and dispatches a CLOSE command to dismiss the dialog. */
    private _onClickSaveAndCloseButton(): void {
        this._onClickSaveButton();
        this.dispatchCommand({
            command: DialogCommandName.CLOSE,
            payload: void 0
        });
    }
}
